import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Route, Calendar, MapPin, Star, Clock } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { rideService } from '@/services/rideService';
import type { Ride } from '@/types';

const PassengerRides: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [rides, setRides] = useState<Ride[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'completed' | 'cancelled'>('all');

  useEffect(() => {
    loadRides();
  }, []);

  const loadRides = async () => {
    if (!currentUser) return;
    try {
      const data = await rideService.getPassengerRides(currentUser.id, 50);
      setRides(data);
    } catch (error) {
      console.error('Error loading rides:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredRides = rides.filter(ride => {
    if (activeTab === 'all') return true;
    return ride.status === activeTab;
  });

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; label: string }> = {
      completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'مكتملة' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'ملغاة' },
      searching: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'جاري البحث' },
      driver_assigned: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'تم التعيين' },
      in_progress: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'جارية' },
    };
    return badges[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status };
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 p-4">
          <button
            onClick={() => navigate('/passenger/home')}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors"
          >
            <ArrowRight className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">سجل الرحلات</h1>
        </div>
      </header>

      <main className="p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          {[
            { key: 'all', label: 'الكل' },
            { key: 'completed', label: 'مكتملة' },
            { key: 'cancelled', label: 'ملغاة' },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key as any)}
              className={`px-4 py-2 rounded-xl font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-primary-500 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Rides List */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredRides.length === 0 ? (
          <div className="text-center py-12">
            <Route className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">لا يوجد رحلات</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRides.map((ride) => {
              const status = getStatusBadge(ride.status);
              return (
                <div 
                  key={ride.id} 
                  className="bg-white rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/passenger/ride/${ride.id}`)}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                      <span className="text-gray-400 text-sm" dir="ltr">
                        #{ride.id.slice(-8).toUpperCase()}
                      </span>
                    </div>
                    <span className="font-bold">{ride.finalPrice || ride.estimatedPrice} ج.س</span>
                  </div>

                  {/* Locations */}
                  <div className="space-y-3 mb-4">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">من</p>
                        <p className="font-medium">{ride.pickup?.address || 'غير محدد'}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-4 h-4 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">إلى</p>
                        <p className="font-medium">{ride.destination?.address || 'غير محدد'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-4 border-t">
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-4 h-4" />
                        {new Date(ride.createdAt).toLocaleDateString('ar-SD')}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(ride.createdAt).toLocaleTimeString('ar-SD', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    {ride.status === 'completed' && ride.driverRating && (
                      <div className="flex items-center gap-1 text-yellow-500">
                        <Star className="w-4 h-4 fill-yellow-500" />
                        <span className="font-medium">{ride.driverRating}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default PassengerRides;
